import { useState, type ReactNode } from "react";
import type { ExportTokenView } from "../api/types.js";
import { Async, Failure } from "./Async.jsx";
import { CopyField } from "./CopyField.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { useSubmit } from "../app/use-submit.js";
import { longDate } from "../lib/format.js";

/**
 * The credential a machine reads this company's signed agreements with.
 *
 * WHAT IT IS FOR. Keeping your own copy of every contract somebody signs —
 * in Dropbox, in a backup, in an accounts package — which means something has
 * to fetch them, and that something has to authenticate.
 *
 * WHY IT IS NOT A PASSWORD. The obvious way to do this is to put a person's
 * portal email and password in a settings file on the machine doing the
 * copying. That credential can do everything that person can do, cannot be
 * withdrawn without locking them out of the portal, and never expires. This
 * one lists signed agreements and downloads them, full stop, and is withdrawn
 * in one press.
 *
 * NEXT TO THE SIGNING LINK, because the two are the same story read in both
 * directions: that one publishes the agreement, this one brings the signed
 * copies back.
 */
export function ExportCredentials({ canEdit }: { readonly canEdit: boolean }): ReactNode {
  // NOTHING AT ALL for somebody who may not edit, rather than a section that
  // loads and fails. Listing these needs `org-settings` read, which a surveyor
  // does not have, so asking anyway would put a red box on their Company
  // screen that reads as a fault and is in fact the permission working.
  if (!canEdit) return null;
  return <Credentials />;
}

function Credentials(): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<ExportTokenView[]>(
    () => client.exportTokens(),
    [client],
  );

  return (
    <>
      <h2>Keeping your own copies</h2>
      <p className="hint small">
        A credential for a program on your own computer that copies every
        signed agreement into a folder as it arrives. It can read your signed
        agreements and nothing else — not a survey, not a job, not anybody
        else&rsquo;s company — and revoking it locks nobody out of here.
      </p>
      <Async state={state} what="your export credentials" reload={reload}>
        {(tokens) => <Body tokens={tokens} reload={reload} />}
      </Async>
    </>
  );
}

function Body({
  tokens,
  reload,
}: {
  readonly tokens: readonly ExportTokenView[];
  readonly reload: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { submit, busy, error } = useSubmit();
  const [label, setLabel] = useState("Dropbox sync on the office PC");
  /** Held in state because the server keeps only a fingerprint of it. */
  const [made, setMade] = useState<string | null>(null);

  const live = tokens.filter((t) => t.revokedAt === null);

  const mint = async (): Promise<void> => {
    await submit(async () => {
      const created = await client.mintExportToken(label.trim());
      setMade(created.secret);
      reload();
    });
  };

  const revoke = async (id: string): Promise<void> => {
    await submit(async () => {
      await client.revokeExportToken(id);
      reload();
    });
  };

  return (
    <>
      {made === null ? null : (
        <div className="notice" role="status">
          <p>
            <strong>Here it is — this once.</strong> Only a fingerprint of it is
            kept, so it cannot be shown again. Paste it into the program that
            will use it now; if it is lost, revoke it below and make another.
          </p>
          <CopyField
            label="Your export credential"
            value={made}
            copies="export credential"
            hint="Treat it as a password: anyone holding it can download every agreement you have had signed."
          />
          <p className="row">
            <button type="button" className="secondary" onClick={() => setMade(null)}>
              I have saved it
            </button>
          </p>
        </div>
      )}

      {live.length === 0 ? (
        <p className="hint small">Nothing is copying your agreements yet.</p>
      ) : (
        <ul className="plain-list" data-testid="export-tokens">
          {live.map((token) => (
            <li key={token.id} className="row-between">
              <span>
                <strong>{token.label}</strong>
                <span className="small muted">
                  {" "}
                  — made {longDate(token.createdAt)}.{" "}
                  {/*
                    WHEN IT LAST READ ANYTHING, because that is the question
                    somebody about to revoke one actually has: "is this still
                    in use, and will I break something?" A label alone cannot
                    answer it once there are two.
                  */}
                  {token.lastUsedAt === null
                    ? "It has not been used yet."
                    : `Last used ${longDate(token.lastUsedAt)}.`}
                </span>
              </span>
              <button
                type="button"
                className="secondary"
                disabled={busy}
                onClick={() => void revoke(token.id)}
              >
                Revoke
              </button>
            </li>
          ))}
        </ul>
      )}

      {/*
        NOT A <form>, for the same reason AgreementLinks is not: this sits
        inside the Company screen, which is itself one form, and a nested form
        is invalid HTML -- the browser drops the inner one and this button
        would submit the OUTER form, saving the company profile instead of
        minting a credential.
      */}
      <div className="stacked">
        <div className="field">
          <label htmlFor="export-token-label">What will hold it</label>
          <input
            id="export-token-label"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              e.preventDefault();
              if (!busy && label.trim() !== "") void mint();
            }}
          />
          <p className="hint small">
            Name the machine or the program, so that whoever revokes this later
            knows what they are cutting off.
          </p>
        </div>
        <button
          type="button"
          disabled={busy || label.trim() === ""}
          onClick={() => void mint()}
        >
          {busy ? "Making…" : "Make an export credential"}
        </button>
      </div>
      <Failure error={error} what="that export credential" />
    </>
  );
}
